/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { FC } from 'react'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import {
  findAllAdditionalTests,
  findAllAdditionalTestsVariables,
  findAllAdditionalTests_findAllAdditionalTest_items as AdditionalTest,
} from '__generatedTypes__/findAllAdditionalTests'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import Table, { TableProps } from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { FIND_ALL_ADDITIONAL_TESTS } from 'queries/additionalTests'
import Loader from 'components/Loader'
import { convertToDollars, priceFormatter } from 'utils/priceFormatter'
import * as classes from './styles'

type Props = {
  platform: Platform
}

export const List: FC<Props> = ({ platform }) => {
  const { getLink } = useLink()

  const { data, loading } = useQuery<findAllAdditionalTests, findAllAdditionalTestsVariables>(
    FIND_ALL_ADDITIONAL_TESTS,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          where: {
            subject: {
              platform: {
                id: platform.id,
              },
            },
          },
        },
      },
    }
  )

  if (loading) {
    return <Loader />
  }

  const productColumns: TableProps<AdditionalTest>['columns'] = [
    {
      title: 'Name',
      renderCell: ({ name }) => <Typography variant="body2">{name}</Typography>,
      flex: '2',
    },
    {
      title: 'Status',
      renderCell: ({ status }) => <Typography variant="body2">{status}</Typography>,
      flex: '1',
    },
    {
      title: 'Price',
      renderCell: ({ price }) => (
        <Typography variant="body2">{priceFormatter.format(convertToDollars(price))}</Typography>
      ),
      flex: '1',
    },
    {
      title: 'Active',
      renderCell: ({ active }) => <Typography variant="body2">{active}</Typography>,
      flex: '1',
    },
    {
      renderCell: ({ id }) => (
        <Link
          to={getLink(cmsRoutes.PRODUCTS_ADDITIONAL_TESTS_EDIT, {
            id,
          })}
        >
          <IconButton size="small" css={classes.link}>
            <EditIcon />
          </IconButton>
        </Link>
      ),
      flex: '0 1 44px',
    },
  ]

  const items = data?.findAllAdditionalTest?.items || []

  return <Table columns={productColumns} items={items} />
}
